import React from 'react';
import { Box, Image, Text, VStack, Icon, Flex, IconButton, Link } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaPlay, FaDownload, FaFilePdf } from 'react-icons/fa';

const formatFileSize = (bytes) => {
  if (!bytes) return 'N/A';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDimensions = (width, height, unit = 'px') => {
  if (!width || !height) return 'N/A';
  if (unit === 'in') return `${width}" x ${height}"`;
  return `${width} x ${height}px`;
};

export const MediaCard = ({ media, onPreview, onDownload }) => {
  const isVideo = media.mediaType === 'video';
  const isPdf = media.fileType === 'pdf';
  const thumbnailUrl = media.thumbnailPath || (!isVideo && !isPdf ? media.filePath : null);

  const handleDownload = (e) => {
    e.stopPropagation();
    if (onDownload) {
      onDownload(media.id);
    }
  };

  const handlePreview = () => {
    if (onPreview) {
      onPreview(media);
    }
  };

  return (
    <Box overflow="hidden" border="1px solid" borderColor="brand.dkBlue">
      <Box
        position="relative"
        paddingTop="66.67%"
        bg="brand.ltGray2"
        cursor="pointer"
        onClick={handlePreview}
        _hover={{ '& .play-overlay': { bg: 'blackAlpha.500' } }}
      >
        {thumbnailUrl ? (
          <>
            <Image
              src={thumbnailUrl}
              alt={media.title}
              position="absolute"
              top={0}
              left={0}
              w="100%"
              h="100%"
              objectFit="cover"
              objectPosition="center top"
              fallbackSrc="https://via.placeholder.com/400x267?text=No+Preview"
            />
            {isVideo && (
              <Flex
                className="play-overlay"
                position="absolute"
                top={0}
                left={0}
                w="100%"
                h="100%"
                align="center"
                justify="center"
                bg="blackAlpha.300"
                transition="background 0.2s"
              >
                <Flex
                  align="center"
                  justify="center"
                  w="50px"
                  h="50px"
                  bg="whiteAlpha.900"
                  borderRadius="full"
                >
                  <Icon as={FaPlay} color="brand.ltRed" boxSize={5} ml={1} />
                </Flex>
              </Flex>
            )}
            {!isVideo && (
              <Flex
                className="play-overlay"
                position="absolute"
                top={0}
                left={0}
                w="100%"
                h="100%"
                align="center"
                justify="center"
                bg="blackAlpha.0"
                transition="background 0.2s"
                _groupHover={{ bg: 'blackAlpha.300' }}
              />
            )}
          </>
        ) : isPdf ? (
          <Flex
            className="play-overlay"
            position="absolute"
            top={0}
            left={0}
            w="100%"
            h="100%"
            align="center"
            justify="center"
            bg="brand.dkGray2"
            transition="background 0.2s"
          >
            <Icon as={FaFilePdf} color="red.300" boxSize={12} />
          </Flex>
        ) : (
          <Flex
            className="play-overlay"
            position="absolute"
            top={0}
            left={0}
            w="100%"
            h="100%"
            align="center"
            justify="center"
            bg="brand.dkGray2"
            transition="background 0.2s"
          >
            <Flex
              align="center"
              justify="center"
              w="60px"
              h="60px"
              bg="whiteAlpha.900"
              borderRadius="full"
            >
              <Icon as={FaPlay} color="brand.ltRed" boxSize={6} ml={1} />
            </Flex>
          </Flex>
        )}
      </Box>

      <Flex p={3} bg="brand.dkBlue" color="brand.white" align="flex-end" justify="space-between" gap={2}>
        <Box flex={1} minW={0}>
          <Link
            as={RouterLink}
            to={`/asset/${media.id}`}
            fontWeight="600"
            fontSize="sm"
            noOfLines={2}
            mb={1}
            display="block"
            color="inherit"
            _hover={{ textDecoration: 'underline' }}
          >
            {media.title}
          </Link>
          <VStack align="flex-start" spacing={0} fontSize="xs" opacity={0.9}>
            <Text>Type: {media.fileType?.toUpperCase()}</Text>
            <Text>Size: {formatFileSize(media.fileSize)}</Text>
            <Text>Dimensions: {formatDimensions(media.width, media.height, media.dimensionUnit)}</Text>
          </VStack>
        </Box>
        <IconButton
          icon={<FaDownload />}
          aria-label="Download"
          size="sm"
          variant="outline"
          color="brand.white"
          borderColor="brand.white"
          _hover={{ bg: 'whiteAlpha.200' }}
          onClick={handleDownload}
          flexShrink={0}
        />
      </Flex>
    </Box>
  );
};
